/**
 * White-label dark-theme derivation (Wave 6 branding v2).
 *
 * From a single brand background colour, derive a full, READABLE dark palette: a contrast-safe
 * text colour (WCAG relative luminance), slightly-offset surfaces for cards/sidebar, a border,
 * and a muted text tone. Pure + side-effect-free so it can be reasoned about and tested.
 *
 * The GZOO default background is treated as "no override" (returns null) so unbranded orgs keep
 * their hand-tuned theme untouched.
 */

export const GZOO_DEFAULT_BACKGROUND = '#030712';

// The branded background themes the DARK theme, and the app is full of hardcoded light text
// (dark:text-white …) that assumes a dark canvas. So a brand background must itself be dark —
// a too-light value is rejected rather than rendered with invisible text.
export const MAX_BG_LUMINANCE = 0.22;

export function isBackgroundTooLight(hex: string): boolean {
  const rgb = hexToRgb(hex);
  return !!rgb && luminance(rgb) > MAX_BG_LUMINANCE;
}

type RGB = [number, number, number];

export function hexToRgb(hex: string): RGB | null {
  const m = (hex || '').trim().replace(/^#/, '');
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}

function rgbToHex([r, g, b]: RGB): string {
  return '#' + [r, g, b].map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('');
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function luminance([r, g, b]: RGB): number {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

function mix(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t] as RGB;
}

/** "H S% L%" channel string (the format shadcn CSS vars expect). */
export function rgbToHslChannels([r, g, b]: RGB): string {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
      case gn: h = (bn - rn) / d + 2; break;
      default: h = (rn - gn) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export interface DerivedTheme {
  /** Hex values for the @theme brand-dark vars (sidebar / brand-dark surfaces). */
  brandBg: string;
  brandSurface: string;
  /** shadcn dark-mode CSS vars, in "H S% L%" channels. */
  vars: Record<string, string>;
}

/**
 * Derive a readable dark palette from a brand background. Returns null for an invalid colour
 * or the GZOO default (= leave the stock theme alone). Text colour is chosen for contrast, so
 * a light brand colour yields dark text and vice-versa.
 */
export function deriveDarkTheme(backgroundHex: string | undefined): DerivedTheme | null {
  if (!backgroundHex) return null;
  if (backgroundHex.trim().toLowerCase() === GZOO_DEFAULT_BACKGROUND) return null;
  const bg = hexToRgb(backgroundHex);
  if (!bg) return null;

  const lum = luminance(bg);
  if (lum > MAX_BG_LUMINANCE) return null; // too light for the dark theme — don't apply
  const LIGHT_TEXT: RGB = [248, 250, 252]; // slate-50
  const DARK_TEXT: RGB = [11, 18, 32]; // near GZOO ink
  const fg = lum < 0.4 ? LIGHT_TEXT : DARK_TEXT;

  const surface = mix(bg, fg, 0.06); // cards / sidebar — a touch toward the text
  const surface2 = mix(bg, fg, 0.1); // muted / secondary / hover
  const border = mix(bg, fg, 0.16);
  const mutedFg = mix(fg, bg, 0.38); // muted text — readable but quieter

  return {
    brandBg: rgbToHex(bg),
    brandSurface: rgbToHex(surface),
    vars: {
      '--background': rgbToHslChannels(bg),
      '--foreground': rgbToHslChannels(fg),
      '--card': rgbToHslChannels(surface),
      '--card-foreground': rgbToHslChannels(fg),
      '--popover': rgbToHslChannels(surface),
      '--popover-foreground': rgbToHslChannels(fg),
      '--secondary': rgbToHslChannels(surface2),
      '--secondary-foreground': rgbToHslChannels(fg),
      '--muted': rgbToHslChannels(surface2),
      '--muted-foreground': rgbToHslChannels(mutedFg),
      '--accent': rgbToHslChannels(surface2),
      '--accent-foreground': rgbToHslChannels(fg),
      '--border': rgbToHslChannels(border),
      '--input': rgbToHslChannels(border),
    },
  };
}
